/**
 * Override-state helper (referenced by Phase 9 of the section-properties
 * overhaul; created in Phase 3 because the inspector rows already need
 * per-row override booleans).
 *
 * Single source of truth used by:
 *   - inspector rows (override-dot + active state)
 *   - the Reset-all-overrides footer pill (visibility + count badge)
 *   - the panel-top-zone breadcrumb suffix `· N overrides` (Phase 9)
 *
 * Decorative-SVG insertions and FormBuilder edits are content mutations on
 * `section.html` and are deliberately excluded from this count. Element style
 * overrides are included.
 */

import {
  SPACING_OVERRIDE_FIELDS,
  getElementOverrideCount,
  getSectionOverrideCount,
  isSet
} from './apply-section-override.js';
import { RESPONSIVE_VIEWPORTS } from '../../editing/section-utils.js';

/**
 * Compute per-row override state for a section.
 *
 * @param {object | null} section
 * @returns {{
 *   background: boolean,
 *   spacing: boolean,
 *   css: boolean,
 *   columns: boolean,
 *   entrance: boolean,
 *   spacingFields: { marginTop: boolean, marginBottom: boolean,
 *                    paddingTop: boolean, paddingBottom: boolean },
 *   elements: { active: boolean, count: number },
 *   total: number
 * }}
 */
export function getOverrideState(section) {
  const overrides = section && section.overrides ? section.overrides : {};

  const spacingFields = {};
  let spacingAny = false;
  const responsive = RESPONSIVE_VIEWPORTS.map((viewport) => overrides.responsive?.[viewport] || {});
  for (const field of SPACING_OVERRIDE_FIELDS) {
    const set = isSet(overrides[field]) || responsive.some((bucket) => isSet(bucket[field]));
    spacingFields[field] = set;
    if (set) spacingAny = true;
  }
  const background =
    isSet(overrides.backgroundColor) || responsive.some((bucket) => isSet(bucket.backgroundColor));
  const css = isSet(overrides.customCSS) || responsive.some((bucket) => isSet(bucket.customCSS));
  const columns =
    isSet(overrides.columnCount) || responsive.some((bucket) => isSet(bucket.columnCount));
  // Entrance (EA-1). Deliberately NOT read from the responsive buckets: the
  // entrance preset has no per-breakpoint form, so a `revealPreset` sitting in
  // one would be a malformed write the normalizer removes, not an override to
  // advertise a dot for.
  const entrance = isSet(overrides.revealPreset);
  const elementCount = getElementOverrideCount(section);

  return {
    background,
    spacing: spacingAny,
    css,
    columns,
    entrance,
    spacingFields,
    elements: { active: elementCount > 0, count: elementCount },
    total: getSectionOverrideCount(section)
  };
}
